import { supabase } from '@/utils/supabase';

export interface ActivityLogEntry {
  id: string;
  organization_id: string;
  user_id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  changes: UserChangeLog[] | null;
  metadata: Record<string, unknown>;
  created_at: string;
  // Joined data
  actor?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

export interface UserChangeLog {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

class ActivityLogService {
  async logUserChange(
    targetUserId: string,
    action: 'created' | 'updated' | 'deleted' | 'status_changed' | 'role_changed' | 'compensation_changed',
    changes?: UserChangeLog[],
    metadata?: Record<string, unknown>
  ) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
return;
}

      const { data: profile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', session.user.id)
        .single();

      if (!profile) {
return;
}

      const { error } = await supabase
        .from('activity_logs')
        .insert({
          organization_id: profile.organization_id,
          user_id: session.user.id,
          entity_type: 'user',
          entity_id: targetUserId,
          action,
          changes: changes || null,
          metadata: metadata || {}
        });

      if (error) {
        // Fail silently if activity_logs table doesn't exist
        // This is not critical for the application to function
        if (error.code !== '42P01') { // 42P01 = table does not exist
          console.error('Error logging activity:', error);
        }
      }
    } catch (error) {
      console.error('Error in logUserChange:', error);
    }
  }

  async getUserChangeHistory(userId: string) {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select(`
          *,
          actor:users!activity_logs_user_id_fkey(
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('entity_type', 'user')
        .eq('entity_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching activity logs:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserChangeHistory:', error);
      return [];
    }
  }

  async logProfileUpdate(userId: string, oldData: Record<string, unknown>, newData: Record<string, unknown>) {
    const changes: UserChangeLog[] = [];
    
    // Track field changes
    const fieldsToTrack = [
      'first_name',
      'last_name',
      'email',
      'phone',
      'role',
      'roles',
      'employment_status',
      'nmls_number',
      'profile_photo_facial'
    ];

    fieldsToTrack.forEach(field => {
      if (field === 'roles' && Array.isArray(oldData[field]) && Array.isArray(newData[field])) {
        // Special handling for arrays
        const oldRoles = oldData[field].sort().join(', ');
        const newRoles = newData[field].sort().join(', ');
        if (oldRoles !== newRoles) {
          changes.push({
            field,
            oldValue: oldRoles,
            newValue: newRoles
          });
        }
      } else if (oldData[field] !== newData[field]) {
        changes.push({
          field,
          oldValue: oldData[field],
          newValue: newData[field]
        });
      }
    });

    if (changes.length > 0) {
      // Determine the main action type
      let action: string = 'updated';
      
      if (changes.some(c => c.field === 'role' || c.field === 'roles')) {
        action = 'role_changed';
      } else if (changes.some(c => c.field === 'employment_status')) {
        action = 'status_changed';
      }

      await this.logUserChange(userId, action as 'updated' | 'role_changed' | 'status_changed', changes);
    }
  }

  formatActionType(action: string): string {
    const actionMap: { [key: string]: string } = {
      created: 'Account Created',
      updated: 'Profile Updated',
      status_changed: 'Status Changed',
      role_changed: 'Role Changed',
      deleted: 'Account Deleted'
    };

    return actionMap[action] || action;
  }

  formatFieldName(field: string): string {
    const fieldMap: { [key: string]: string } = {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      role: 'Primary Role',
      roles: 'Roles',
      employment_status: 'Employment Status',
      nmls_number: 'NMLS #',
      profile_photo_facial: 'Profile Picture',
      license_states: 'Licensed States',
      mortgage_specialties: 'Mortgage Specialties',
      website: 'Website',
      linkedin_url: 'LinkedIn',
      facebook_url: 'Facebook',
      has_specific_brand: 'Has Specific Brand',
      brand_name: 'Brand Name',
      bio: 'Biography'
    };

    return fieldMap[field] || field;
  }

  formatValue(field: string, value: unknown): string {
    if (value === null || value === undefined) {
return 'Not set';
}
    
    if (field === 'employment_status') {
      if (value === 'active') {
return 'Active';
}
      if (value === 'pending') {
return 'Pending';
}
      if (value === 'terminated') {
return 'Terminated';
}
      return String(value); // Fallback for unknown values
    }
    
    if (field === 'role') {
      return value === 'loan_officer' ? 'Loan Officer' : 
             value.charAt(0).toUpperCase() + value.slice(1);
    }
    
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    return String(value);
  }
}

export const activityLogService = new ActivityLogService();